import { PERMISSIONS } from "@shared/constants/permissions";
import DeleteIcon from "@shared/svg/delete-document.svg?react";
import { AlertBox } from "@shared/ui/AlertBox";
import InfoBanner, { INFO_BANNER_TYPE } from "@shared/ui/InfoBanner";
import Toast, { TOAST_TYPES } from "@shared/ui/Toast";
import Tooltip from "@shared/ui/Tooltip";
import { SearchInput } from "@shared/ui/Inputs";
import { MinusCircle } from "phosphor-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AutoSizer, List as ListRV } from "react-virtualized";

import getEnum from "$/settings/enums";
import PAID_FEATURES from "$/settings/paid-features.json";
import { NotFoundComponent } from "~/components/_notFoundMessage";
import { useAuth } from "~/components/general";
import { PartRow } from "~/components/inventory/_inventoryPartRow";
import PartPreview from "~/components/inventory/PartPreview";
import { ITEMS_PER_PAGE } from "~/constants";
import usePermissions from "~/hooks/usePermissions";
import { NoUserAssignedIcon } from "~/icons/icons";
import { useRemoveAssetPart } from "~/services/asset";
import { getAllParts } from "~/services/inventory/_query";
import { useGetAppConfigValue } from "~/utils/appFeatures";
import isPaidFeatureAvailable from "~/utils/isPaidFeatureAvailable";

import MachinePartsHead from "./MachinePartsHead";
import type { MachinePartsProps, Part } from "./types";
import { UserType } from "@shared/types/user";

const PaidFeatures = getEnum(PAID_FEATURES, "reference");

const MachineParts = ({
  parts = [],
  isDetached = false,
  partsCustomAdditionalFields = [],
  isSharedAssetsPage = false,
  asset,
}: MachinePartsProps) => {
  const { removePart: removeAssetPart } = useRemoveAssetPart();
  const { user } = useAuth() as { user: UserType };
  const { canChange } = usePermissions();
  const appConfig = useGetAppConfigValue();
  const { messages } = useIntl();
  const params = useParams<{ id: string }>();

  const isPMEAvailable = isPaidFeatureAvailable(
    PaidFeatures.preventiveMaintenance,
    user,
    appConfig,
  );

  const selectedPart = useRef<Part | null>(null);
  const removePartRef = useRef<Part | null>(null);
  const partTableHeadRef = useRef<HTMLDivElement>(null);

  const [searchValue, setSearchValue] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [showPartPreview, setShowPartPreview] = useState(false);
  const [showDetachTemplateAlert, setShowDetachTemplateAlert] = useState(false);
  const [listHeight, setListHeight] = useState(0);
  const [partRemoveConfirmation, setPartRemoveConfirmation] = useState({
    name: "",
    show: false,
  });

  const {
    parts: allParts,
    fetchingParts,
    handleFetchMore,
    totalCount,
  } = getAllParts({
    limit: ITEMS_PER_PAGE,
    skip: 0,
    searchQuery,
  });

  const handleScrollBottom = useCallback(
    (event?: { previousPosition?: string }) => {
      if (
        allParts.length >= ITEMS_PER_PAGE &&
        !fetchingParts &&
        event?.previousPosition !== "above" &&
        totalCount > allParts.length
      ) {
        handleFetchMore({
          limit: ITEMS_PER_PAGE,
          skip: allParts.length,
        });
      }
    },
    [allParts.length, fetchingParts, totalCount, handleFetchMore],
  );

  const filteredParts = useMemo(() => {
    if (!searchValue) return parts || [];
    const query = searchValue.toLowerCase();
    return (parts || []).filter(
      (item) =>
        item?.part?.name?.toLowerCase().includes(query) ||
        item?.part?.articleNumber?.toLowerCase().includes(query),
    );
  }, [searchValue, parts]);

  const isCreatedFromTemplate = !!asset?.template;
  const isAttachedToProduct =
    isCreatedFromTemplate && !asset?.detachedFromTemplate?.["inventoryParts"];

  // Measure after layout settles when parts load or template banner appears/disappears.
  // requestAnimationFrame ensures conditional elements above (InfoBanner, SearchInput)
  // have rendered before we read getBoundingClientRect.
  useEffect(() => {
    let rafId: number;
    const update = () => {
      if (partTableHeadRef.current) {
        const bottom = partTableHeadRef.current.getBoundingClientRect().bottom;
        setListHeight(Math.max(0, window.innerHeight - bottom - 16));
      }
    };
    rafId = requestAnimationFrame(update);
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [parts?.length, isCreatedFromTemplate, isSharedAssetsPage]);

  const handleNameClick = useCallback(
    (id: string) => {
      const partIndex = parts.findIndex(({ part }) => part._id === id);
      if (partIndex !== -1) {
        selectedPart.current = parts[partIndex].part;
        setShowPartPreview(true);
      }
    },
    [parts],
  );

  const handleDeleteClick = useCallback((part: Part) => {
    removePartRef.current = part;
    setPartRemoveConfirmation({ show: true, name: part.name });
  }, []);

  const handleDeleteCancel = useCallback(() => {
    removePartRef.current = null;
    setPartRemoveConfirmation({ show: false, name: "" });
  }, []);

  const handleDeleteAccept = useCallback(async () => {
    try {
      await removeAssetPart({
        assetId: params?.id,
        partId: removePartRef.current?._id,
      });
      toast(<Toast message={messages?.machines?.["machinePartRemoved"]} />, {
        closeButton: false,
      });
    } catch {
      toast(
        <Toast
          type={TOAST_TYPES.ERROR}
          message={messages["hierarchy"]["assets"]["partRemoveFailure"]}
        />,
        { closeButton: false },
      );
    } finally {
      removePartRef.current = null;
      setPartRemoveConfirmation({ show: false, name: "" });
    }
  }, [removeAssetPart, params?.id, messages]);

  return (
    <>
      {!isSharedAssetsPage && isCreatedFromTemplate && (
        <InfoBanner
          message={
            isAttachedToProduct
              ? messages["hierarchy"]["assets"]["productAssetMessages"]["parts"]
              : messages["hierarchy"]["assets"]["detachedMessages"]["parts"]
          }
          bannerType={
            isAttachedToProduct
              ? INFO_BANNER_TYPE.SECONDARY
              : INFO_BANNER_TYPE.PRIMARY
          }
        />
      )}

      {parts?.length > 0 && (
        <SearchInput
          onChange={setSearchValue}
          placeholder={messages["hierarchy"]["assets"]["searchParts"]}
          value={searchValue}
          activeByDefault
          activeClassName="w-full"
          className="mb-md"
        />
      )}

      <MachinePartsHead
        machineParts={parts || []}
        allParts={allParts}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onScrollToBottom={handleScrollBottom}
        partsLoading={fetchingParts}
        showDetachAlert={!isAttachedToProduct}
        isDetached={isDetached}
        totalCount={totalCount}
        showDetachTemplateAlert={showDetachTemplateAlert}
        setShowDetachTemplateAlert={setShowDetachTemplateAlert}
        onDeletePartDetachConfirm={handleDeleteAccept}
        isSharedAssetsPage={isSharedAssetsPage}
      />

      {!parts?.length && (
        <NotFoundComponent
          icon={<NoUserAssignedIcon />}
          title={messages["hierarchy"]["assets"]["noParts"]}
        />
      )}

      {parts?.length > 0 && (
        <section className="px-0 overflow-x-hidden no-scrollbar">
          <div
            ref={partTableHeadRef}
            className="border-0 flex font-manrope font-medium text-xs text-secondary capitalize border-b border-solid border-primary"
          >
            <div className="relative py-md px-sm leading-[18px] w-1/4">
              {messages?.inventory?.["labels"].name}
            </div>
            <div className="relative py-md leading-[18px] w-5/12 !px-0">
              {messages?.inventory?.["labels"].description}
            </div>
            <div className="relative py-md leading-[18px] w-1/4 !px-0">
              {messages?.inventory?.["labels"].articleNumber}
            </div>
            {!isSharedAssetsPage && (
              <div className="relative py-md px-sm leading-[18px] w-1/12 text-center">
                {messages?.inventory?.["action"]}
              </div>
            )}
          </div>

          {!filteredParts.length && searchValue && (
            <NotFoundComponent
              icon={<NoUserAssignedIcon />}
              title={messages["common"]["noResultFound"]}
            />
          )}

          <div style={{ width: "calc(100% + 24px)", height: listHeight }}>
            <AutoSizer disableHeight>
              {({ width }) => (
                <ListRV
                  height={listHeight}
                  width={width}
                  rowCount={filteredParts.length}
                  rowHeight={62}
                  rowRenderer={({ index, key, style }) => {
                    const { part } = filteredParts[index];
                    const isInheritedFromTemplate = parts.find(
                      (p) => p.part?._id === part._id,
                    )?.inheritedFromTemplate;

                    return (
                      <div key={key} style={style} className="pr-md">
                        <PartRow
                          key={key}
                          part={part}
                          handlers={{ handleNameClick, handleDeleteClick }}
                          showEdit={false}
                          customAction={
                            !canChange(
                              PERMISSIONS.assets.update,
                              asset?.createdBy,
                            ) || isSharedAssetsPage ? null : (
                              <>
                                <div
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    if (isInheritedFromTemplate) return;
                                    handleDeleteClick(part);
                                  }}
                                  className="w-lg h-lg"
                                  data-tip={messages["hierarchy"]["assets"][
                                    "productAssetMessages"
                                  ]["partsTooltip"].replace(
                                    "{{}}",
                                    asset?.template?.title,
                                  )}
                                  data-for={`delete-part-${part._id}`}
                                >
                                  <MinusCircle
                                    className={
                                      isInheritedFromTemplate
                                        ? "text-disabled"
                                        : "text-secondary"
                                    }
                                    size={16}
                                  />
                                </div>
                                {isInheritedFromTemplate && (
                                  <Tooltip
                                    className="max-w-[30%] z-50"
                                    id={`delete-part-${part._id}`}
                                    place="left"
                                  />
                                )}
                              </>
                            )
                          }
                          isSharedAssetsPage={isSharedAssetsPage}
                        />
                      </div>
                    );
                  }}
                />
              )}
            </AutoSizer>
          </div>
        </section>
      )}

      {showPartPreview && (
        <PartPreview
          data={selectedPart.current}
          onClose={() => setShowPartPreview(false)}
          customAdditionalFields={partsCustomAdditionalFields}
          isOpen={showPartPreview}
          showCustomFields={true}
          isSharedAssetsPage={isSharedAssetsPage}
        />
      )}

      <AlertBox
        isOpen={partRemoveConfirmation.show}
        title={messages["hierarchy"]["assets"][
          isPMEAvailable
            ? "unassignConfirmationTitleWithPM"
            : "unassignConfirmationTitle"
        ].replace("{{}}", partRemoveConfirmation.name)}
        image={<DeleteIcon width="130" height="130" />}
        acceptButtonText={
          messages?.inventory?.["unassignConfirmationAcceptBtnText"]
        }
        cancelButtonText={
          messages?.inventory?.["unassignConfirmationCencalBtnText"]
        }
        onAccept={handleDeleteAccept}
        onCancel={handleDeleteCancel}
      />
    </>
  );
};

export default MachineParts;
